import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { showToast } from "../toastService";
import { apiFetch } from "../utils/api";
import Header from "../components/Header";
import BottomNavbar from "../components/BottomNavbar";
import "./css/Settings.css";

export default function Settings() {
  const navigate = useNavigate();
  const [lang] = useState(localStorage.getItem("app_language") || "en");
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [logoutLoading, setLogoutLoading] = useState(false);
  const [contactUsLoading, setContactUsLoading] = useState(false);

  const AUTH_BASE_URL = process.env.REACT_APP_AUTH_BASE_URL;
  const USER_BASE_URL = process.env.REACT_APP_USER_BASE_URL;
  const VERSION = process.env.REACT_APP_VERSION;

  // Translations
  const t = {
    en: {
      generalSettings: "General Settings",
      profile: "Profile",
      pastWorks: "Past Works",
      notificationPreferences: "Notification Preferences",
      languagePreferences: "Language Preferences",
      more: "More",
      privacyPolicy: "Privacy Policy",
      termsConditions: "Terms & Conditions",
      shippingPolicy: "Shipping Policy",
      refundPolicy: "Refund Policy",
      contactUs: "Contact Us",
      logOut: "Log Out",
      logoutConfirm: "Are you sure you want to log out?",
      cancel: "Cancel",
      confirm: "Confirm",
      loggingOut: "Logging out...",
      loadingContact: "Loading contact details...",
      noContactDetails: "No contact details found",
    },
    ar: {
      generalSettings: "الإعدادات العامة",
      profile: "الملف الشخصي",
      pastWorks: "الأعمال السابقة",
      notificationPreferences: "تفضيلات الإشعارات",
      languagePreferences: "تفضيلات اللغة",
      more: "المزيد",
      privacyPolicy: "سياسة الخصوصية",
      termsConditions: "الشروط والأحكام",
      shippingPolicy: "سياسة الشحن",
      refundPolicy: "سياسة الاسترجاع",
      contactUs: "اتصل بنا",
      logOut: "تسجيل الخروج",
      logoutConfirm: "هل أنت متأكد أنك تريد تسجيل الخروج؟",
      cancel: "إلغاء",
      confirm: "تأكيد",
      loggingOut: "جاري تسجيل الخروج...",
      loadingContact: "جاري تحميل تفاصيل الاتصال...",
      noContactDetails: "لم يتم العثور على تفاصيل الاتصال",
    },
  }[lang];

  // Handle logout confirmation
  const handleLogoutClick = () => {
    setShowLogoutModal(true);
  };

  // Handle logout
  const handleLogout = async () => {
    setLogoutLoading(true);
    const accessToken = localStorage.getItem("accessToken");

    if (accessToken) {
      try {
        await fetch(`${AUTH_BASE_URL}/${VERSION}/user/logout`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });
      } catch (error) {
        console.error("Logout API failed:", error);
      }
    }

    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("isAuthenticated");
    localStorage.removeItem("username");

    setLogoutLoading(false);
    showToast(lang === "ar" ? "تم تسجيل الخروج بنجاح!" : "Logged out successfully!", "success");
    navigate("/");
  };

  // Handle policy link click
  const handlePolicyClick = (policyName) => {
    const baseUrl = "https://fixora-app.com/policies/";
    const policyUrl = `${baseUrl}${policyName}`;
    window.open(policyUrl, "_blank");
  };

  // Handle contact us click
  const handleContactUs = async () => {
    try {
      setContactUsLoading(true);
      const data = await apiFetch(
        `${USER_BASE_URL}/${VERSION}/profile/app-details`,
        "GET",
        null,
        {}
      );

      if (data && data.response_code === 200 && data.response) {
        const contactDetails = data.response.contact_details || {};
        const email = contactDetails.email;
        const phone = contactDetails.phone;

        if (!email && !phone) {
          showToast(t.noContactDetails, "error");
          return;
        }

        // If email exists, open email client
        if (email) {
          window.location.href = `mailto:${email}`;
        } else if (phone) {
          // If only phone exists, show it (or you could use tel: link)
          window.location.href = `tel:${phone}`;
        }
      } else {
        showToast(t.noContactDetails, "error");
      }
    } catch (err) {
      console.error("Error fetching contact details:", err);
      showToast(t.noContactDetails, "error");
    } finally {
      setContactUsLoading(false);
    }
  };

  // Handle settings item click (for future implementation)
  const handleSettingsClick = (item) => {
    if (item === "profile") {
      navigate("/profile");
    } else if (item === "past-works") {
      navigate("/past-works");
    } else if (item === "notification-preferences") {
      navigate("/work-preferences");
    } else if (item === "language-preferences") {
      navigate("/language-preferences");
    } else if (item === "contact-us") {
      handleContactUs();
    } else {
      // Will be implemented later
      console.log("Navigate to:", item);
    }
  };

  return (
    <div className="settings-container" dir={lang === "ar" ? "rtl" : "ltr"}>
      {/* Header */}
      <Header lang={lang} />

      {/* Main Content */}
      <div className="settings-content">
        {/* General Settings */}
        <div className="settings-section">
          <h2 className="settings-section-title">{t.generalSettings}</h2>
          <div className="settings-list">
            <div
              className="settings-item"
              onClick={() => handleSettingsClick("profile")}
            >
              <i className="bx bx-user"></i>
              <span className="settings-item-text">{t.profile}</span>
              <i className="bx bx-chevron-right settings-arrow"></i>
            </div>
            <div
              className="settings-item"
              onClick={() => handleSettingsClick("past-works")}
            >
              <i className="bx bx-briefcase"></i>
              <span className="settings-item-text">{t.pastWorks}</span>
              <i className="bx bx-chevron-right settings-arrow"></i>
            </div>
            <div
              className="settings-item"
              onClick={() => handleSettingsClick("notification-preferences")}
            >
              <i className="bx bx-bell"></i>
              <span className="settings-item-text">{t.notificationPreferences}</span>
              <i className="bx bx-chevron-right settings-arrow"></i>
            </div>
            <div
              className="settings-item"
              onClick={() => handleSettingsClick("language-preferences")}
            >
              <i className="bx bx-globe"></i>
              <span className="settings-item-text">{t.languagePreferences}</span>
              <i className="bx bx-chevron-right settings-arrow"></i>
            </div>
          </div>
        </div>

        {/* More Section */}
        <div className="settings-section">
          <h2 className="settings-section-title">{t.more}</h2>
          <div className="settings-list">
            <div
              className="settings-item"
              onClick={() => handlePolicyClick("privacy-policy")}
            >
              <i className="bx bx-shield"></i>
              <span className="settings-item-text">{t.privacyPolicy}</span>
              <i className="bx bx-chevron-right settings-arrow"></i>
            </div>
            <div
              className="settings-item"
              onClick={() => handlePolicyClick("terms-conditions")}
            >
              <i className="bx bx-file"></i>
              <span className="settings-item-text">{t.termsConditions}</span>
              <i className="bx bx-chevron-right settings-arrow"></i>
            </div>
            <div
              className="settings-item"
              onClick={() => handlePolicyClick("shipping-policy")}
            >
              <i className="bx bx-package"></i>
              <span className="settings-item-text">{t.shippingPolicy}</span>
              <i className="bx bx-chevron-right settings-arrow"></i>
            </div>
            <div
              className="settings-item"
              onClick={() => handlePolicyClick("refund-policy")}
            >
              <i className="bx bx-building"></i>
              <span className="settings-item-text">{t.refundPolicy}</span>
              <i className="bx bx-chevron-right settings-arrow"></i>
            </div>
            <div
              className="settings-item"
              onClick={() => handleSettingsClick("contact-us")}
              style={{ opacity: contactUsLoading ? 0.6 : 1, pointerEvents: contactUsLoading ? "none" : "auto" }}
            >
              <i className="bx bx-headphone"></i>
              <span className="settings-item-text">
                {contactUsLoading ? t.loadingContact : t.contactUs}
              </span>
              {contactUsLoading ? (
                <span className="contact-spinner"></span>
              ) : (
                <i className="bx bx-chevron-right settings-arrow"></i>
              )}
            </div>
            <div className="settings-item logout-item" onClick={handleLogoutClick}>
              <i className="bx bx-log-out"></i>
              <span className="settings-item-text">{t.logOut}</span>
              <i className="bx bx-chevron-right settings-arrow"></i>
            </div>
          </div>
        </div>
      </div>

      {/* Logout Confirmation Modal */}
      {showLogoutModal && (
        <div
          className="logout-modal-overlay"
          onClick={() => setShowLogoutModal(false)}
        >
          <div
            className="logout-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="logout-modal-title">{t.logoutConfirm}</h3>
            <div className="logout-modal-buttons">
              <button
                className="logout-modal-btn cancel-btn"
                onClick={() => setShowLogoutModal(false)}
                disabled={logoutLoading}
              >
                {t.cancel}
              </button>
              <button
                className="logout-modal-btn confirm-btn"
                onClick={handleLogout}
                disabled={logoutLoading}
              >
                {logoutLoading ? (
                  <>
                    <span className="logout-spinner"></span>
                    {t.loggingOut}
                  </>
                ) : (
                  t.confirm
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bottom Navigation */}
      <BottomNavbar currentPage="settings" lang={lang} />
    </div>
  );
}

